import { Link } from "react-router-dom"
import { ArrowRight, type LucideIcon } from "lucide-react"

import { Button } from "@/components/ui/button"
import { useSession, type Entitlements } from "@/hooks/use-session"

type FeatureKey = keyof Entitlements["features"]

// FeatureGuard renders its children when the org's plan includes `feature`,
// otherwise an upgrade interstitial. Used to wrap gated page routes so a
// non-entitled user gets a clear "upgrade to unlock" instead of an empty page
// or a raw API error.
export function FeatureGuard({
  feature,
  title,
  description,
  icon: Icon,
  tier = "stream",
  children,
}: {
  feature: FeatureKey
  title: string
  description: string
  icon: LucideIcon
  tier?: "stream" | "surge" | "torrent"
  children: React.ReactNode
}) {
  const session = useSession()
  if (session.status === "loading") return null

  const features =
    session.status === "authenticated" ? session.user.entitlements?.features : undefined
  // Entitled unless the flag is explicitly false (don't block while unknown).
  const entitled = !features || features[feature] !== false
  if (entitled) return <>{children}</>

  const planName = tier === "torrent" ? "Torrent" : tier === "surge" ? "Surge" : "Stream"
  const includedOn =
    tier === "torrent"
      ? "Available on the Torrent plan"
      : tier === "surge"
        ? "Included on Surge and Torrent"
        : "Included on Stream and Torrent"

  return (
    <div className="mx-auto flex max-w-md flex-col items-center py-20 text-center">
      <div className="mb-5 flex size-14 items-center justify-center rounded-2xl bg-rose-500/10 text-rose-500">
        <Icon className="size-7" />
      </div>
      <h2 className="text-xl font-semibold tracking-tight">{title}</h2>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{description}</p>
      {tier === "torrent" ? (
        <Button asChild className="group mt-6 bg-rose-600 text-white hover:bg-rose-500">
          <a href="https://hivehook.com/enterprise" target="_blank" rel="noopener noreferrer">
            Talk to us about Torrent
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </a>
        </Button>
      ) : (
        <Button asChild className="group mt-6 bg-rose-600 text-white hover:bg-rose-500">
          <Link to="/upgrade">
            Upgrade to {planName}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </Button>
      )}
      <p className="mt-3 text-2xs uppercase tracking-[0.18em] text-muted-foreground/50">
        {includedOn}
      </p>
    </div>
  )
}
